import { Injectable } from '@angular/core';
import { API } from './api';
import { BaseProvider } from './base';
import { BrainVersionType } from './brain';
import { Dataset } from './dataset';
import { EventName, Events } from '../utils/events';
import { ModalProvider } from './popup/modal';
import { TranslateService } from '@ngx-translate/core';

/**
 * Indicates whether a test set has been created for a dataset
 */
export enum TestSetState {
    /**
     * No test set has yet been created for the dataset
     */
    None = 'None',

    /**
     * A test set is in the process of being created for the dataset
     * and test assets are being uploaded
     */
    Uploading = 'Uploading',

    /**
     * A test set has been created for the dataset
     */
    Created = 'Created',

    /**
     * A test set is out of sync and we are re-running accuracy on it
     */
    Updating = 'Updating'
}

export interface BaseTestSetResult {
    testSetIdentifier: string;
    testSetDescription: string;
    brainVersionType: BrainVersionType;
    testImagesCount: number;

    /**
     * track whether results are obsolete (when newer brain version is available)
     */
    isDirty: boolean;
}

export interface RecognitionTestSetResult extends BaseTestSetResult {
    accuracy: number;
    precision: number;
    recall: number;
}

export interface DetectionTestSetResult extends BaseTestSetResult {
    averagePrecision: number;
    averageRecall: number;
    miapVOC: number;
    miapCOCO: number;
}

@Injectable()
export class TestSetProvider extends BaseProvider {
    constructor(
        public api: API,
        private events: Events,
        private modalProvider: ModalProvider,
        private translate: TranslateService
    ) {
        super();
    }

    public async getRecognitionTestSetResult(dataset: Dataset): Promise<RecognitionTestSetResult | null> {
        let response;

        try {
            response = await this.api.get(`datasets/${dataset.id}/test-set/results`);
        } catch (err) {
            if (err.status == 404) {
                // No test set has been created
                return null;
            }

            throw err;
        }

        return response.message;
    }

    public async getDetectionTestSetResult(dataset: Dataset): Promise<DetectionTestSetResult | null> {
        let response;

        try {
            response = await this.api.get(`datasets/${dataset.id}/test-set/results`);
        } catch (err) {
            if (err.status == 404) {
                // No test set has been created
                return null;
            }

            throw err;
        }

        return response.message;
    }

    public async deleteTestSet(dataset: Dataset): Promise<void> {
        await this.api.delete(`datasets/${dataset.id}/test-set`);

        this.events.publish(EventName.TestSetDeleted, dataset.id);
    }

    public async rerunAccuracy(dataset: Dataset): Promise<void> {
        this.events.publish(EventName.TestSetUpdating, dataset.id);

        try {
            await this.api.post(`datasets/${dataset.id}/test-set/run`);
        } finally {
            // For now, throw the same event even if an error occurs
            // to allow subscribers to reset any state
            this.events.publish(EventName.TestSetUpdated, dataset.id);
        }
    }

    public async confirmDeleteTestSet(): Promise<boolean> {
        return await this.modalProvider.presentConfirmationPrompt(
            this.translate.instant('Delete current test image(s) / results?'),
            this.translate.instant('Are you sure you would like to delete the current test image(s)?'),
            this.translate.instant('Delete')
        );
    }
}



// WEBPACK FOOTER //
// ./src/providers/test-set.ts